
import { Component, Vue, Watch } from 'vue-property-decorator';
import FullCalendar, { CalendarOptions } from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import deLocale from '@fullcalendar/core/locales/de';
import { ICalendar } from 'datebook';

@Component({
  components: {
    FullCalendar,
  },
})
export default class App extends Vue {
  selectedPlace: { name: string, id: string } | null = null;

  selectedArea: { strasse: string, id: string, revier: string } | null = null;

  places: { name: string, id: string }[] = [];

  areas: { strasse: string, id: string, revier: string }[] = [];

  events = [];

  loading = false;

  saveSelection = false;

  localStorageAvailable = false;

  showPrintButton = false;

  get calendarOptions(): CalendarOptions {
    return {
      plugins: [dayGridPlugin, listPlugin],
      initialView: 'dayGridMonth',
      viewDidMount: this.onViewDidMount,
      locale: deLocale,
      weekNumbers: this.windowWidth > 500,
      weekends: this.windowWidth > 500,
      events: this.events,
      headerToolbar: {
        start: 'title',
        center: 'dayGridMonth,listYear',
        end: this.windowWidth > 500 ? 'today prev,next' : 'prev,next',
      },
      buttonText: {
        today: 'Aktueller Monat',
        listYear: 'Jahr',
      },
    };
  }

  onViewDidMount(arg: {view: {type: string}}): void {
    const view: string = arg.view.type;
    if (view === 'dayGridMonth') {
      this.showPrintButton = false;
    } else {
      this.showPrintButton = true;
    }
  }

  async mounted(): Promise<void> {
    this.localStorageAvailable = this.isLocalStorageAvailable();
    this.loading = true;

    try {
      const result = await window.fetch('/kalender/places', {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const places: { name: string, id: string }[] = await result.json();
      this.places = places;
      this.loading = false;
    } catch (e) {
      this.loading = false;
    }

    if (this.localStorageAvailable) {
      const selectedPlace = localStorage.getItem('selectedPlace');
      if (selectedPlace !== null) {
        this.selectedPlace = JSON.parse(selectedPlace);
      }

      const saveSelection = localStorage.getItem('saveSelection');
      if (saveSelection !== null) {
        this.saveSelection = JSON.parse(saveSelection);
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const placeId = urlParams.get('ort');
    if (placeId !== null) {
      this.selectedPlace = this.places.find((place) => place.id === placeId) || null;
    }
  }

  @Watch('selectedPlace')
  async onSelectedPlaceChanged(): Promise<void> {
    if (this.selectedPlace === null) {
      return;
    }

    this.selectedArea = null;
    this.areas = [];
    this.events = [];
    this.loading = true;

    if (this.saveSelection && this.localStorageAvailable) {
      localStorage.setItem('selectedPlace', JSON.stringify(this.selectedPlace));
    }

    try {
      const result = await window.fetch(`/kalender/areas/${this.selectedPlace.id}`, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const areas: { strasse: string, id: string, revier: string }[] = await result.json();
      this.areas = areas;

      if (!this.areas.length) {
        // Load events for place without areas
        await this.getEvents();
      }

      if (this.localStorageAvailable) {
        const selectedArea = localStorage.getItem('selectedArea');
        if (selectedArea !== null) {
          this.selectedArea = JSON.parse(selectedArea);
        }
      }

      this.loading = false;
    } catch (e) {
      this.loading = false;
    }
  }

  @Watch('selectedArea')
  async onSelectedAreaChanged(): Promise<void> {
    if (this.selectedArea === null) {
      return;
    }
    this.loading = true;
    this.events = [];

    if (this.saveSelection && this.localStorageAvailable) {
      localStorage.setItem('selectedArea', JSON.stringify(this.selectedArea));
    }

    try {
      await this.getEvents();
      this.loading = false;
    } catch (e) {
      this.loading = false;
    }
  }

  @Watch('saveSelection')
  onSaveSelectionChanged(): void {
    if (!this.saveSelection && this.localStorageAvailable) {
      localStorage.removeItem('selectedArea');
      localStorage.removeItem('selectedPlace');
      localStorage.removeItem('saveSelection');
      return;
    }

    localStorage.setItem('saveSelection', JSON.stringify(this.saveSelection));
    if (this.selectedPlace && this.localStorageAvailable) {
      localStorage.setItem('selectedPlace', JSON.stringify(this.selectedPlace));
    }
    if (this.selectedArea && this.localStorageAvailable) {
      localStorage.setItem('selectedArea', JSON.stringify(this.selectedArea));
    }
  }

  async getEvents(): Promise<void> {
    let area = '0';
    if (this.selectedPlace === null) {
      return;
    }
    if (this.selectedArea !== null) {
      area = this.selectedArea.revier;
    }
    const result = await window.fetch(`/kalender/events/${this.selectedPlace.id}/${area}`, {
      method: 'GET',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const events = await result.json();
    this.events = events;
  }

  // eslint-disable-next-line class-methods-use-this
  isLocalStorageAvailable(): boolean {
    const test = 'test';
    try {
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  downloadICal(): void {
    if (!this.events.length || !this.selectedPlace) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const firstEvent = this.events[0] as any;
    const iCalendar = new ICalendar({
      title: firstEvent.title,
      location: this.selectedPlace.name,
      description: firstEvent.title,
      start: new Date(firstEvent.start),
    });

    for (let i = 1; i < this.events.length; i += 1) {
      iCalendar.addEvent(new ICalendar({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        title: (this.events[i] as any).title as string,
        location: this.selectedPlace.name,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        description: (this.events[i] as any).title as string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        start: new Date((this.events[i] as any).start),
      }));
    }

    iCalendar.download(`${this.selectedPlace.name}.ics`);
  }

  // eslint-disable-next-line class-methods-use-this
  onPrintClicked(): void {
    window.print();
  }
}
